import React, { useEffect, useMemo, useState } from "react";
import "./ADInsertClip.css";

const INITIAL_DELAY_MS = 800;
const CARD_ENTER_MS = 720;
const LETTER_STAGGER_MS = 140;
const SOLVED_PAUSE_MS = 320;

const GRID_SIZE = 5;
const KEYWORD = "BUILD";
const WORD_ROW = 2;

/** Dimmed mock layout — swap this div for a real sub-component or iframe later. */
function BackgroundPlaceholder() {
    return (
        <div className="ad-insert-bg-placeholder" data-slot="background-mock">
            <div className="ad-insert-bg-tile ad-insert-bg-tile--wide" />
            <div className="ad-insert-bg-tile" />
            <div className="ad-insert-bg-tile" />
            <div className="ad-insert-bg-tile ad-insert-bg-tile--wide" />
            <div className="ad-insert-bg-tile" />
            <div className="ad-insert-bg-tile" />
            <div className="ad-insert-bg-tile" />
            <div className="ad-insert-bg-tile ad-insert-bg-tile--wide" />
        </div>
    );
}

function SolvedBanner({ visible }) {
    return (
        <div className={`ad-insert-banner${visible ? " ad-insert-banner--visible" : ""}`}>
            <p className="ad-insert-banner-text">Puzzle solved!</p>
        </div>
    );
}

function CrosswordGrid({ revealedCount, wordGlow }) {
    const cells = useMemo(() => {
        const list = [];
        for (let r = 0; r < GRID_SIZE; r += 1) {
            for (let c = 0; c < GRID_SIZE; c += 1) {
                const isWord = r === WORD_ROW && c < KEYWORD.length;
                const letterIndex = isWord ? c : -1;
                list.push({
                    key: `${r}-${c}`,
                    letter: isWord ? KEYWORD[c] : "",
                    isWord,
                    letterIndex,
                });
            }
        }
        return list;
    }, []);

    return (
        <div className="ad-insert-grid" role="grid" aria-label="Crossword preview">
            {cells.map(({ key, letter, isWord, letterIndex }) => {
                const revealed = isWord && letterIndex < revealedCount;
                const glow = wordGlow && isWord;
                const className = [
                    "ad-insert-cell",
                    isWord ? "ad-insert-cell--active" : "",
                    revealed ? "ad-insert-cell--revealed" : "",
                    glow ? "ad-insert-cell--glow" : "",
                ]
                    .filter(Boolean)
                    .join(" ");

                return (
                    <div
                        key={key}
                        className={className}
                        style={
                            isWord && letterIndex >= 0
                                ? { transitionDelay: `${letterIndex * LETTER_STAGGER_MS}ms` }
                                : undefined
                        }
                    >
                        {revealed ? letter : ""}
                    </div>
                );
            })}
        </div>
    );
}

function PopUpCard({ cardVisible, revealedCount, solved }) {
    return (
        <div className="ad-insert-card-wrap">
            <div className={`ad-insert-card${cardVisible ? " ad-insert-card--visible" : ""}`}>
                <SolvedBanner visible={solved} />
                <CrosswordGrid revealedCount={revealedCount} wordGlow={solved} />
                <p className="ad-insert-label">Crossword</p>
            </div>
        </div>
    );
}

/**
 * Self-contained video insert clip for screen recording.
 * Auto-plays on mount after {@link INITIAL_DELAY_MS} — no user interaction.
 */
export function VideoInsertClip() {
    const [cardVisible, setCardVisible] = useState(false);
    const [revealedCount, setRevealedCount] = useState(0);
    const [solved, setSolved] = useState(false);

    useEffect(() => {
        const timers = [];

        timers.push(
            setTimeout(() => {
                setCardVisible(true);
            }, INITIAL_DELAY_MS),
        );

        const revealStart = INITIAL_DELAY_MS + CARD_ENTER_MS + 120;
        for (let i = 0; i < KEYWORD.length; i += 1) {
            timers.push(
                setTimeout(() => {
                    setRevealedCount(i + 1);
                }, revealStart + i * LETTER_STAGGER_MS),
            );
        }

        const solvedAt = revealStart + KEYWORD.length * LETTER_STAGGER_MS + SOLVED_PAUSE_MS;
        timers.push(
            setTimeout(() => {
                setSolved(true);
            }, solvedAt),
        );

        return () => timers.forEach(clearTimeout);
    }, []);

    return (
        <div className="ad-insert-root">
            <div className="ad-insert-stage">
                <div className="ad-insert-bg">
                    <div className="ad-insert-bg-blur">
                        <BackgroundPlaceholder />
                    </div>
                    <div className="ad-insert-vignette" />
                </div>
                <PopUpCard cardVisible={cardVisible} revealedCount={revealedCount} solved={solved} />
            </div>
        </div>
    );
}

/**
 * Route: `/ad/insert` — menu-free 16:9 video insert preview.
 */
export default function ADInsertPreviewPage() {
    return <VideoInsertClip />;
}
